import { AuthProviderEnum, EAuthExceptionEnum, TAuthExceptionEnum } from '@onesource/schemas';
import { MutationCache, QueryCache, QueryClient, QueryKey } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import {
  axiosAbortController,
  getActiveAuthProvider,
  logout,
  redirectForSSOLogin,
  removeTokenFromAxios,
} from './common';
import { enqueueSnackbar } from 'notistack';

declare module '@tanstack/react-query' {
  export interface Register {
    queryMeta: {
      errorMessage?: string;
    };
    mutationMeta: {
      invalidatesQuery?: QueryKey | QueryKey[];
      invalidatesQueries?: QueryKey[];
      successMessage?: string;
      errorMessage?: string;
    };
  }
}

export const queryClient = new QueryClient({
  queryCache: new QueryCache({
    onError: (error: unknown) => {
      const err = error as AxiosError<{ message: TAuthExceptionEnum | string }>;

      if (window && err.response?.status === 401) {
        const authProvider = getActiveAuthProvider();

        // Supabase 401s are handled by the axios response interceptor (token refresh).
        // Only handle WordPress JWT expiry here to avoid double-logout loops.
        if (authProvider === AuthProviderEnum.supabase) {
          return;
        }

        const message = err.response?.data?.message;
        if (message === EAuthExceptionEnum.INVALID_TOKEN || message === EAuthExceptionEnum.TOKEN_EXPIRED) {
          logout();
          axiosAbortController.abort();
          removeTokenFromAxios();
          redirectForSSOLogin(authProvider);
        }
      }
    },
  }),
  mutationCache: new MutationCache({
    onSuccess: (_data, _variables, _context, mutation) => {
      if (mutation.meta?.successMessage) {
        enqueueSnackbar(mutation.meta.successMessage, {
          variant: 'success',
          autoHideDuration: 1250,
          anchorOrigin: {
            vertical: 'bottom',
            horizontal: 'center',
          },
        });
      }
    },
    onError: (_data, _variables, _context, mutation) => {
      if (mutation.meta?.errorMessage) {
        enqueueSnackbar(mutation.meta.errorMessage, {
          variant: 'error',
          autoHideDuration: 1250,
          anchorOrigin: {
            vertical: 'bottom',
            horizontal: 'center',
          },
        });
      }
    },
    onSettled: async (_data, _error, _variables, _context, mutation) => {
      if (mutation.meta?.invalidatesQuery) {
        queryClient.invalidateQueries({
          queryKey: mutation.meta?.invalidatesQuery,
        });
      }

      if (mutation.meta?.invalidatesQueries) {
        mutation.meta?.invalidatesQueries.forEach(async queryKey => {
          await queryClient.invalidateQueries({ queryKey });
        });
      }
    },
  }),
});
